import { Fragment } from 'react'
import type { TmsInvoiceRow } from '@numeo/types-main-web-app'
import type { ListPage, ThreadLoad } from './cardResults'
import { DocumentChips } from './DocumentChips'
import { formatUsd } from './format'
import { LoadFixActions } from './LoadFixActions'
import { PageFooter } from './PageFooter'
import { ReadyLoadItem } from './ReadyLoadItem'
import { documentActions } from './rowActions'
import { isReadyTabRow, StatusBadge } from './StatusBadge'

interface InvoicesTableProps {
    rows: TmsInvoiceRow[]
    /** How much of the list the rows are, when the card said. */
    page?: ListPage
    /** Loads the thread knows by id, for a row from before the card carried its own `load_id`. */
    loads: ThreadLoad[]
    onSend: (message: string) => void
}

const rowKey = (row: TmsInvoiceRow, i: number) => row.invoice_id ?? `${row.load_numbers.join(',')}-${i}`

/**
 * Invoice rows (any tab but Ready) as a table: invoice, loads, broker, amount and the Invoices page group, and for an invoice of one
 * load its documents, with the upload of a missing rate con or POD under it (a factor asks for them after invoicing too).
 */
const InvoiceRowsTable = ({ rows, onSend }: { rows: TmsInvoiceRow[]; onSend: (message: string) => void }) => {
    const withDocuments = rows.some((row) => row.documents)
    const columns = withDocuments ? 6 : 5
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
                <thead className="text-foreground-muted">
                    <tr>
                        <th className="py-1 pr-3 font-medium">Invoice</th>
                        <th className="py-1 pr-3 font-medium">Loads</th>
                        <th className="py-1 pr-3 font-medium">Broker</th>
                        <th className="py-1 pr-3 text-right font-medium">Amount</th>
                        <th className="py-1 pr-3 font-medium">Status</th>
                        {withDocuments && <th className="py-1 font-medium">Documents</th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => {
                        const loadNumber = row.load_numbers.length === 1 ? row.load_numbers[0] : undefined
                        // Once the factor funded it or the broker paid, a document fix changes nothing: the chips stay, the buttons go.
                        const settled = row.status_group === 'funded_by_factor' || row.status_group === 'paid_by_broker'
                        const fixes = loadNumber && !settled ? documentActions(row.documents) : []
                        return (
                            <Fragment key={rowKey(row, i)}>
                                <tr className="border-t border-border">
                                    <td className="py-1.5 pr-3">{row.invoice_number ?? '—'}</td>
                                    <td className="py-1.5 pr-3">{row.load_numbers.map((n) => `#${n}`).join(', ') || '—'}</td>
                                    <td className="py-1.5 pr-3">{row.broker ?? '—'}</td>
                                    <td className="py-1.5 pr-3 text-right tabular-nums">{formatUsd(row.amount_due_usd)}</td>
                                    <td className="py-1.5 pr-3">
                                        <StatusBadge row={row} />
                                    </td>
                                    {withDocuments && (
                                        <td className="py-1.5">
                                            <DocumentChips documents={row.documents} />
                                        </td>
                                    )}
                                </tr>
                                {loadNumber && fixes.length > 0 && (
                                    <tr>
                                        <td colSpan={columns} className="pb-2">
                                            {/* Zero width, full cell: a wide revision opened here scrolls in place instead of widening the table. */}
                                            <div className="w-0 min-w-full overflow-x-auto">
                                                <LoadFixActions loadNumber={loadNumber} loadId={row.load_id} actions={fixes} onSend={onSend} />
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

/**
 * A `tms_invoices.list` answer (plan FR-G3). Ready-tab loads are one list, one item per load, each with what blocks it and the
 * actions that fix it right there; invoice rows stay a table. How much of the list it is, and "Show more", once at the bottom.
 */
export const InvoicesTable = ({ rows, page, loads, onSend }: InvoicesTableProps) => {
    const idOf = (number: string | undefined) => loads.find((load) => load.number === number)?.id
    return (
        <div className="flex flex-col gap-3">
            {rows.some(isReadyTabRow) ? (
                <ul className="flex flex-col divide-y divide-border" aria-label="Loads to invoice">
                    {rows.map((row, i) => (
                        <ReadyLoadItem key={rowKey(row, i)} row={row} loadId={row.load_id ?? idOf(row.load_numbers[0])} onSend={onSend} />
                    ))}
                </ul>
            ) : (
                <InvoiceRowsTable rows={rows} onSend={onSend} />
            )}
            <PageFooter page={page} subject="invoices" onSend={onSend} />
        </div>
    )
}
